"use client";
import clsx from "clsx";
import {m, Variants} from "framer-motion";
import {useEffect, useRef} from "react";
import {useSmoothScroll} from "@/context/SmoothScrollContext";
import { type SectionProps } from  "@/types/types"

export default function AnimatedSection({
  children,
  id,
  delay = 0,
  className,
  scrollTrigger = false,
  exaggerated = false,
}: SectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const {scrollTo} = useSmoothScroll();

  // Optional: Auto-scroll to this section if it's the target of a hash URL
  useEffect(() => {
    if (id && scrollTrigger) {
      const handleHashChange = () => {
        if (window.location.hash === `#${id}`) {
          setTimeout(() => scrollTo(id), 100);
        }
      };

      if (window.location.hash === `#${id}`) {
        setTimeout(() => scrollTo(id), 300);
      }

      window.addEventListener("hashchange", handleHashChange);
      return () => window.removeEventListener("hashchange", handleHashChange);
    }
  }, [id, scrollTo, scrollTrigger]);

  // ✅ Explicitly type variants as Variants
  const variants: Variants = exaggerated
    ? {
        hidden: {opacity: 0, scale: 0.6, rotate: -10, y: 50},
        visible: {
          opacity: 1,
          scale: 1,
          rotate: 0,
          y: 0,
          transition: {
            type: "spring",
            stiffness: 120,
            damping: 12,
            delay,
          },
        },
      }
    : {
        hidden: {opacity: 0, scale: 0.95, y: 20},
        visible: {
          opacity: 1,
          scale: 1,
          y: 0,
          transition: {
            duration: 0.8,
            delay,
            ease: [0.25, 0.46, 0.45, 0.94],
            staggerChildren: 0.1,
          },
        },
      };

  return (
    <m.section
      ref={sectionRef}
      id={id}
      initial="hidden"
      whileInView="visible"
      exit="hidden"
      viewport={{once: true, amount: 0.2}}
      variants={variants}
      className={clsx("w-full scroll-mt-20", className)}
    >
      {children}
    </m.section>
  );
}
